import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useTranslations } from '../translations';
import PrivacyNotice from './PrivacyNotice';
import './GoogleTermsConsentModal.css';

interface GoogleTermsConsentModalProps {
  onConfirm: (marketingEmails: boolean) => Promise<void> | void;
  onCancel: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
}

/**
 * Collects the explicit terms acceptance a brand-new Google account needs.
 *
 * The backend records which exact terms version each user accepted, and that audit
 * record has to be truthful, so an account is never created from a Google token alone.
 */
const GoogleTermsConsentModal: React.FC<GoogleTermsConsentModalProps> = ({
  onConfirm,
  onCancel,
  onNavigateToTerms,
  onNavigateToPrivacy,
}) => {
  const t = useTranslations();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [marketingEmails, setMarketingEmails] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isConfirming) {
        onCancel();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isConfirming, onCancel]);

  const handleConfirm = async () => {
    if (!agreedToTerms) {
      setShowTermsError(true);
      return;
    }
    setIsConfirming(true);
    try {
      await onConfirm(marketingEmails);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="google-consent-overlay">
      <div
        className="google-consent-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="google-consent-title"
      >
        <header className="google-consent-header">
          <span className="google-consent-icon" aria-hidden="true"><ShieldCheck size={22} /></span>
          <div>
            <h2 id="google-consent-title">{t.googleSignIn.consentTitle}</h2>
            <p>{t.googleSignIn.consentDescription}</p>
          </div>
        </header>

        <div className="google-consent-body">
          <label className="google-consent-checkbox">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={event => {
                setAgreedToTerms(event.target.checked);
                if (event.target.checked) {
                  setShowTermsError(false);
                }
              }}
              aria-invalid={showTermsError}
            />
            <span>
              {t.register.termsAndConditions}{' '}
              {onNavigateToTerms ? (
                <button type="button" className="google-consent-link" onClick={onNavigateToTerms}>
                  {t.register.termsLink}
                </button>
              ) : (
                <strong>{t.register.termsLink}</strong>
              )}{' '}
              {t.register.termsText}
              <span className="required-asterisk">*</span>
            </span>
          </label>
          {showTermsError && (
            <p className="google-consent-error" role="alert">{t.googleSignIn.consentRequired}</p>
          )}

          <label className="google-consent-checkbox">
            <input
              type="checkbox"
              checked={marketingEmails}
              onChange={event => setMarketingEmails(event.target.checked)}
            />
            <span>{t.register.marketingEmails}</span>
          </label>

          <PrivacyNotice onNavigateToPrivacy={onNavigateToPrivacy} />
        </div>

        <footer className="google-consent-actions">
          <button
            type="button"
            className="google-consent-cancel"
            onClick={onCancel}
            disabled={isConfirming}
          >
            {t.common.cancel}
          </button>
          <button
            type="button"
            className="google-consent-confirm"
            onClick={() => void handleConfirm()}
            disabled={isConfirming}
          >
            {isConfirming ? t.googleSignIn.consentSubmitting : t.googleSignIn.consentConfirm}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default GoogleTermsConsentModal;
